import React, { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import { TIERS, DEFAULT_UPGRADE_TIER } from '../constants/tiers';
import { formatSavingsBadge, formatPerCardSavings } from '../constants/annualSavings';
import { trackUpgradeWallShown, trackUpgradeWallConverted } from '../lib/analytics';
import { notifyError } from '../lib/notify';
import ExitIntentSurvey from './ExitIntentSurvey';

/**
 * MUNDALO - Upgrade Wall
 * Triggered when a free-trial / expired-trial user clicks a paid-only feature.
 *
 * Context-aware tier list (iter 51): pass `featureId` to scope the visible
 * tiers to only those that actually unlock the clicked feature. When omitted,
 * shows all paid tiers (back-compat).
 *
 * Mapping spec:
 *   blueprint          → blueprint_only, essentials, guided
 *   classroom          → essentials, guided
 *   events / housing
 *     / localDeals     → guided, community, connected
 *   cityGuides         → all paid tiers
 *   (default / unknown)→ all paid tiers
 *
 * Free Trial is NEVER shown as an upgrade option.
 */
const FEATURE_TIER_MAP = {
  blueprint:   ['blueprint_only', 'essentials', 'guided'],
  classroom:   ['essentials', 'guided'],
  events:      ['guided', 'community', 'connected'],
  housing:     ['guided', 'community', 'connected'],
  localDeals:  ['guided', 'community', 'connected'],
  cityGuides:  ['blueprint_only', 'essentials', 'guided', 'community', 'connected'],
};
const ALL_PAID_TIERS = ['blueprint_only', 'essentials', 'guided', 'community', 'connected'];

export default function UpgradeWall({
  open,
  onClose,
  reason,
  featureId,
  recommendedTier = DEFAULT_UPGRADE_TIER,
}) {
  // Tier list scoped to what unlocks the clicked feature. Falls back to all
  // paid tiers when featureId isn't recognized. Free is never included.
  const order = (featureId && FEATURE_TIER_MAP[featureId]) || ALL_PAID_TIERS;

  // If the recommended/pre-selected tier isn't in the filtered list, fall
  // back to the first available tier in the list (so the CTA is always
  // pointing at something the user is actually allowed to buy).
  const initialSelected = order.includes(recommendedTier) ? recommendedTier : order[0];
  const [selected, setSelected] = useState(initialSelected);
  const [billing, setBilling] = useState('monthly'); // 'monthly' | 'annual' - affects community + connected only
  const [showExitSurvey, setShowExitSurvey] = useState(false);

  // If `featureId` changes while the modal is open, re-anchor the selection
  // so we never leave the user on a hidden tier.
  useEffect(() => {
    if (!order.includes(selected)) setSelected(order[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureId]);

  // GA4 - fire upgrade_wall_shown when the modal mounts (exactly once per open)
  useEffect(() => {
    if (open) {
      trackUpgradeWallShown({ feature: reason || 'unknown' });
    }
  }, [open, reason]);

  const handleClose = () => {
    setShowExitSurvey(true);
  };
  const handleSurveyDismiss = () => {
    setShowExitSurvey(false);
    onClose?.();
  };

  if (!open) return null;

  const isSubTier = (id) => id === 'community' || id === 'connected';
  const showAnnualFor = (id) => isSubTier(id) && billing === 'annual';

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="upgrade-wall"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A1F2E]/60 backdrop-blur-sm px-4"
    >
      <div className="relative w-full max-w-4xl bg-[#F9F6EF] rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        {/* Close */}
        <button
          data-testid="upgrade-wall-close"
          onClick={handleClose}
          className="absolute top-5 right-5 p-2 rounded-full hover:bg-[#F4ECD8] transition"
          aria-label="Close upgrade wall"
        >
          <X className="w-5 h-5 text-[#0A1F2E]" />
        </button>

        {/* Header */}
        <div className="px-8 pt-10 pb-6 text-center">
          <p className="font-mono uppercase tracking-[0.08em] text-xs text-[#1A5C6E] mb-3">
            Unlock the next step
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-[#0A1F2E] leading-tight">
            {reason ? <span className="italic">{reason}</span> : <>Pick the plan that <span className="italic">moves you</span></>}
          </h2>
          <p className="text-[#0A1F2E]/70 mt-3 max-w-xl mx-auto">
            Choose the plan that's right for you.
          </p>
        </div>

        {/* Monthly / Annual toggle - only relevant when subscription tiers are visible */}
        {order.some(isSubTier) && (
        <div
          className="mx-auto mb-4 max-w-xs flex items-center justify-center bg-white rounded-full p-1 border border-[#EFE6CF] shadow-sm"
          role="tablist"
          aria-label="Billing period"
          data-testid="upgrade-wall-billing-toggle"
        >
          <button
            type="button"
            role="tab"
            aria-selected={billing === 'monthly'}
            onClick={() => setBilling('monthly')}
            data-testid="upgrade-wall-billing-monthly"
            className={`flex-1 py-2 rounded-full font-mono uppercase text-[10px] tracking-[0.12em] transition-all ${
              billing === 'monthly'
                ? 'bg-[#F9F6EF] text-[#0A1F2E] border-2 border-[#2CA8A0] font-semibold'
                : 'text-[#0A1F2E]/60'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={billing === 'annual'}
            onClick={() => setBilling('annual')}
            data-testid="upgrade-wall-billing-annual"
            className={`flex-1 py-2 rounded-full font-mono uppercase text-[10px] tracking-[0.12em] transition-all flex items-center justify-center gap-1.5 ${
              billing === 'annual'
                ? 'bg-[#F9F6EF] text-[#0A1F2E] border-2 border-[#2CA8A0] font-semibold'
                : 'text-[#0A1F2E]/60'
            }`}
          >
            Annual
            <span className="inline-flex items-center bg-[#E8A830] text-[#0A1F2E] text-[8px] font-bold px-1.5 py-0.5 rounded-full">
              {formatSavingsBadge()}
            </span>
          </button>
        </div>
        )}

        {/* Tier grid */}
        <div className={`px-6 md:px-8 pb-8 grid grid-cols-1 gap-3 ${
          order.length <= 2 ? 'md:grid-cols-2'
          : order.length === 3 ? 'md:grid-cols-3'
          : order.length === 4 ? 'md:grid-cols-4'
          : 'md:grid-cols-5'
        }`}>
          {order.map((tierId) => {
            const t = TIERS[tierId];
            const isSelected = selected === tierId;
            const isPrimary = t.primary;
            const ringClass = isSelected
              ? 'ring-2 ring-[#E8A830] shadow-[0_0_0_4px_rgba(232,168,48,0.18)]'
              : 'ring-1 ring-[#EFE6CF] hover:ring-[#2CA8A0]';

            return (
              <button
                key={tierId}
                data-testid={`upgrade-tier-${tierId}`}
                onClick={() => setSelected(tierId)}
                className={`text-left bg-white rounded-xl p-5 transition-all relative ${ringClass}`}
              >
                {t.badge && isPrimary && (
                  <span
                    className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E8A830] text-[#0A1F2E] text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-full whitespace-nowrap"
                  >
                    {t.badge}
                  </span>
                )}
                <h3 className="font-display text-2xl text-[#0A1F2E] mb-1">{t.name}</h3>
                <div className="flex items-baseline gap-1 mb-1">
                  <span
                    className="font-display text-3xl text-[#0A1F2E]"
                    data-testid={`upgrade-tier-${tierId}-price`}
                  >
                    ${showAnnualFor(tierId) ? t.priceAnnual : t.price}
                  </span>
                  <span className="text-xs text-[#0A1F2E]/60">/{showAnnualFor(tierId) ? 'year' : t.interval}</span>
                </div>
                {showAnnualFor(tierId) && formatPerCardSavings(tierId) && (
                  <p className="text-[10px] font-mono uppercase tracking-[0.12em] text-[#E8A830] font-semibold mb-2">
                    {formatPerCardSavings(tierId)}
                  </p>
                )}
                <p className="text-sm text-[#0A1F2E]/70 leading-snug">{t.description}</p>

                {isSelected && (
                  <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#E8A830] flex items-center justify-center">
                    <Check className="w-4 h-4 text-[#0A1F2E]" strokeWidth={3} />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* CTA strip */}
        <div className="border-t border-[#EFE6CF] px-6 md:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-4 bg-[#FFFFFF] rounded-b-2xl">
          <div className="text-sm text-[#0A1F2E]/70">
            Selected: <span className="font-medium text-[#0A1F2E]">{TIERS[selected].name}</span>
            {' · '}
            <span className="text-[#0A1F2E]/60">
              ${showAnnualFor(selected) ? TIERS[selected].priceAnnual : TIERS[selected].price}{' '}
              {showAnnualFor(selected) ? '/year' : TIERS[selected].interval}
            </span>
          </div>
        <a
          data-testid="upgrade-wall-continue"
          href="#"
          onClick={async (e) => {
            e.preventDefault();
            try {
              const tier = selected;
              const isSub = tier === 'community' || tier === 'connected';
              const billingPeriod = isSub ? billing : 'onetime';
              trackUpgradeWallConverted({ feature: reason || 'unknown', target_tier: tier });
              const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/checkout`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json',
                  ...(localStorage.getItem('auth_token') ? { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } : {}),
                },
                body: JSON.stringify({
                  tier,
                  billing_period: billingPeriod,
                  success_path: '/portal/dashboard',
                  cancel_path: '/portal/upgrade',
                }),
              });
              const data = await r.json();
              if (data.url) {
                window.location.href = data.url;
              } else {
                notifyError(data, 'Could not start checkout - please try again.');
              }
            } catch (err) {
              notifyError('Network error - please try again.');
            }
          }}
          className="inline-flex items-center justify-center bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-medium px-7 py-3 rounded-full transition-all cursor-pointer"
        >
          Continue with {TIERS[selected].name} →
        </a>
        </div>
      </div>
      <ExitIntentSurvey
        open={showExitSurvey}
        reason={reason}
        onDismiss={handleSurveyDismiss}
      />
    </div>
  );
}
